import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths } from 'date-fns';
import { Calendar as CalendarIcon, Filter, X, Sparkles, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/context/ThemeContext';

// Indian Financial Year helpers
const getIndianFYStart = (year) => new Date(year, 3, 1);
const getIndianFYEnd = (year) => new Date(year + 1, 2, 31, 23, 59, 59);

const getCurrentIndianFY = () => {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};

const generateFYOptions = () => {
  const currentFY = getCurrentIndianFY();
  const options = [];
  for (let year = currentFY + 1; year >= 2020; year--) {
    options.push({
      value: `fy-${year}`,
      label: `FY ${year}-${(year + 1).toString().slice(-2)}`,
      start: getIndianFYStart(year),
      end: getIndianFYEnd(year)
    });
  }
  return options;
};

const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const date = subMonths(now, i);
    options.push({
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMM yyyy'),
      start: startOfMonth(date),
      end: endOfMonth(date)
    });
  }
  return options;
};

const QUICK_FILTERS = [
  { value: 'today', label: 'Today', icon: '📅' },
  { value: 'this-week', label: 'This Week', icon: '📆' },
  { value: 'this-month', label: 'This Month', icon: '🗓️' },
  { value: 'this-year', label: 'This Year', icon: '🎯' },
];

const DATE_FIELDS = [
  { value: 'due_on', label: 'Due On (Deadline)' },
  { value: 'file_received_on', label: 'Delivered On' },
  { value: 'accepted_on', label: 'Accepted On' },
];

const FloatingFilterPanel = ({ onFilterChange, showDateFieldSelector = true }) => {
  const { themeMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedFY, setSelectedFY] = useState('');
  const [dateField, setDateField] = useState('due_on');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  
  const isGlass = themeMode === 'glass';
  const fyOptions = useMemo(() => generateFYOptions(), []);
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  
  const applyFilter = (type, value, start, end) => {
    if (type !== 'quick') setQuickFilter('');
    if (type !== 'month') setSelectedMonth('');
    if (type !== 'fy') setSelectedFY('');
    if (type !== 'custom') {
      setCustomFrom(null);
      setCustomTo(null);
    }
    
    onFilterChange({
      dateField,
      startDate: start,
      endDate: end,
      filterType: type,
      filterValue: value
    });
  };
  
  const handleQuickFilter = (value) => {
    setQuickFilter(value);
    const now = new Date();
    let start, end;
    
    switch (value) {
      case 'today':
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case 'this-week':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'this-month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'this-year':
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      default:
        return;
    }
    
    applyFilter('quick', value, start, end);
  };
  
  const handleMonthFilter = (value) => {
    setSelectedMonth(value);
    const option = monthOptions.find(m => m.value === value);
    if (option) {
      applyFilter('month', value, option.start, option.end);
    }
  };
  
  const handleFYFilter = (value) => {
    setSelectedFY(value);
    const option = fyOptions.find(f => f.value === value);
    if (option) {
      applyFilter('fy', value, option.start, option.end);
    }
  };
  
  const handleCustomRange = () => {
    if (customFrom && customTo) {
      applyFilter('custom', 'custom', startOfDay(customFrom), endOfDay(customTo));
    }
  };
  
  const clearFilters = () => {
    setQuickFilter('');
    setSelectedMonth('');
    setSelectedFY('');
    setCustomFrom(null);
    setCustomTo(null);
    onFilterChange(null);
  };
  
  const hasActiveFilter = quickFilter || selectedMonth || selectedFY || (customFrom && customTo);
  
  const getActiveFilterLabel = () => {
    if (quickFilter) return QUICK_FILTERS.find(f => f.value === quickFilter)?.label;
    if (selectedMonth) return monthOptions.find(m => m.value === selectedMonth)?.label;
    if (selectedFY) return fyOptions.find(f => f.value === selectedFY)?.label;
    if (customFrom && customTo) return `${format(customFrom, 'dd MMM')} - ${format(customTo, 'dd MMM')}`;
    return null;
  };
  
  return (
    <>
      {/* Floating Action Button */}
      <motion.div
        className="fixed bottom-24 right-6 z-50"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.5 }}
      >
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300",
            isOpen 
              ? "bg-gradient-to-br from-red-500 to-pink-500" 
              : hasActiveFilter 
                ? "bg-gradient-to-br from-green-500 to-emerald-500"
                : "bg-gradient-to-br from-violet-500 to-purple-600"
          )}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          data-testid="filter-toggle-btn"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            key={isOpen ? 'close' : 'filter'}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Filter className="w-6 h-6 text-white" />
            )}
          </motion.div>
          
          {/* Active filter indicator */}
          {hasActiveFilter && !isOpen && (
            <motion.div
              className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring" }}
            >
              <Sparkles className="w-3 h-3 text-yellow-800" />
            </motion.div>
          )}
        </motion.button>
      </motion.div>
      
      {/* Filter Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-44 right-6 z-40 w-80"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <div className={cn(
              "rounded-2xl shadow-2xl overflow-hidden",
              isGlass 
                ? "glass-panel border border-white/50" 
                : "bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-700"
            )}>
              {/* Header */}
              <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <Zap className="w-5 h-5 text-yellow-300" />
                    </motion.div>
                    <h3 className="font-semibold text-white">Smart Filters</h3>
                  </div>
                  {hasActiveFilter && (
                    <motion.button
                      onClick={clearFilters}
                      className="text-xs text-white/80 hover:text-white flex items-center gap-1 px-2 py-1 rounded-full bg-white/20"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <X className="w-3 h-3" />
                      Clear
                    </motion.button>
                  )}
                </div>
                
                {/* Active filter badge */}
                {hasActiveFilter && (
                  <motion.div
                    className="mt-2 px-3 py-1.5 bg-white/20 rounded-full text-sm text-white inline-flex items-center gap-2"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    {getActiveFilterLabel()}
                  </motion.div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-5 space-y-5">
                {/* Date Field Selector */}
                {showDateFieldSelector && (
                  <motion.div 
                    className="space-y-2"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filter By</label>
                    <Select value={dateField} onValueChange={setDateField}>
                      <SelectTrigger className="w-full h-10 rounded-xl border-gray-200 focus:ring-violet-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_FIELDS.map(field => (
                          <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </motion.div>
                )}
                
                {/* Quick Filters */}
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Quick Select</label>
                  <div className="grid grid-cols-2 gap-2">
                    {QUICK_FILTERS.map((filter, idx) => (
                      <motion.button
                        key={filter.value}
                        onClick={() => handleQuickFilter(filter.value)}
                        className={cn(
                          "flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                          quickFilter === filter.value
                            ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-200"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        )}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + idx * 0.05 }}
                      >
                        <span>{filter.icon}</span>
                        <span>{filter.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
                
                {/* Month Selector */}
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">By Month</label>
                  <Select value={selectedMonth} onValueChange={handleMonthFilter}>
                    <SelectTrigger className={cn(
                      "w-full h-10 rounded-xl border-gray-200",
                      selectedMonth && "border-violet-400 ring-2 ring-violet-100"
                    )}>
                      <SelectValue placeholder="Select month..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {monthOptions.map(month => (
                        <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>
                
                {/* Financial Year */}
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 }}
                >
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Financial Year</label>
                  <Select value={selectedFY} onValueChange={handleFYFilter}>
                    <SelectTrigger className={cn(
                      "w-full h-10 rounded-xl border-gray-200",
                      selectedFY && "border-violet-400 ring-2 ring-violet-100"
                    )}>
                      <SelectValue placeholder="Select FY..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fyOptions.map(fy => (
                        <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>
                
                {/* Custom Date Range */}
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Custom Range</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
                      <PopoverTrigger asChild>
                        <button className={cn(
                          "flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition-all",
                          customFrom 
                            ? "border-violet-400 bg-violet-50 text-violet-700" 
                            : "border-gray-200 text-gray-600 hover:bg-gray-50"
                        )}>
                          <CalendarIcon className="w-4 h-4" />
                          {customFrom ? format(customFrom, 'dd MMM') : 'From'}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customFrom}
                          onSelect={(date) => { setCustomFrom(date); setShowFromCalendar(false); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    
                    <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
                      <PopoverTrigger asChild>
                        <button className={cn(
                          "flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition-all",
                          customTo 
                            ? "border-violet-400 bg-violet-50 text-violet-700" 
                            : "border-gray-200 text-gray-600 hover:bg-gray-50"
                        )}>
                          <CalendarIcon className="w-4 h-4" />
                          {customTo ? format(customTo, 'dd MMM') : 'To'}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={customTo}
                          onSelect={(date) => { setCustomTo(date); setShowToCalendar(false); }}
                          disabled={(date) => customFrom && date < customFrom}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {customFrom && customTo && (
                    <motion.button
                      onClick={handleCustomRange}
                      className="w-full py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-medium shadow-lg shadow-violet-200"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Apply Custom Range
                    </motion.button>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingFilterPanel;
